import { parseCSV, autoDetectColumns, type ColumnMapping } from './shipmentImportService';
import type { PurchaseOrderItem, InventoryItem } from '../types';

export interface POImportRow {
  productName: string;
  quantity: number;
  unit?: InventoryItem['unit'];
  costPerUnit: number;
}

export interface POImportColumnMapping {
  name: string;
  quantity: string;
  unit?: string;
  cost?: string;
}

const UNITS: InventoryItem['unit'][] = ['kg', 'g', 'L', 'ml', 'units'];

function parseUnit(raw: string | undefined): InventoryItem['unit'] | undefined {
  if (!raw) return undefined;
  const n = raw.trim().toLowerCase();
  if (n === 'unit' || n === 'each' || n === 'ea') return 'units';
  return UNITS.find((u) => u === n) ?? (UNITS.includes(raw as InventoryItem['unit']) ? (raw as InventoryItem['unit']) : undefined);
}

export function poAutoDetectColumns(headers: string[]): POImportColumnMapping {
  const base = autoDetectColumns(headers);
  return {
    name: base.name,
    quantity: base.quantity,
    unit: base.unit,
    cost: base.cost || base.totalAmount,
  };
}

export function importPOLinesFromCSV(
  content: string,
  mapping: POImportColumnMapping,
  hasHeader: boolean,
): POImportRow[] {
  const rows = parseCSV(content);
  if (rows.length === 0) return [];

  const headerRow = hasHeader ? rows[0] : null;
  const dataRows = hasHeader ? rows.slice(1) : rows;
  const headers = headerRow ?? rows[0].map((_, i) => `col${i}`);

  const colIndex = (key: string | undefined): number => {
    if (!key) return -1;
    const idx = headers.findIndex((h) => h.trim().toLowerCase() === key.trim().toLowerCase());
    if (idx >= 0) return idx;
    return headers.indexOf(key);
  };

  const nameIdx = colIndex(mapping.name);
  const qtyIdx = colIndex(mapping.quantity);
  const unitIdx = mapping.unit ? colIndex(mapping.unit) : -1;
  const costIdx = mapping.cost ? colIndex(mapping.cost) : -1;

  const result: POImportRow[] = [];
  for (const row of dataRows) {
    const name = nameIdx >= 0 ? String(row[nameIdx] ?? '').trim() : '';
    if (!name) continue;
    const qty = qtyIdx >= 0 ? parseFloat(String(row[qtyIdx] ?? '1')) : 1;
    const cost = costIdx >= 0 ? parseFloat(String(row[costIdx] ?? '0')) : 0;
    const unit = unitIdx >= 0 ? parseUnit(String(row[unitIdx] ?? '')) : undefined;
    result.push({
      productName: name,
      quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
      costPerUnit: Number.isFinite(cost) && cost >= 0 ? cost : 0,
      unit,
    });
  }
  return result;
}

/** Parse tab/comma separated paste: "name, qty, cost" per line */
export function importPOLinesFromPaste(text: string): POImportRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const result: POImportRow[] = [];
  for (const line of lines) {
    const parts = line.includes('\t') ? line.split('\t') : line.split(',').map((p) => p.trim());
    const name = parts[0]?.trim();
    if (!name) continue;
    const qty = parts[1] != null ? parseFloat(parts[1]) : 1;
    const cost = parts[2] != null ? parseFloat(parts[2]) : 0;
    const unit = parts[3] != null ? parseUnit(parts[3]) : undefined;
    result.push({
      productName: name,
      quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
      costPerUnit: Number.isFinite(cost) && cost >= 0 ? cost : 0,
      unit,
    });
  }
  return result;
}

export function poImportRowsToLineItems(rows: POImportRow[]): PurchaseOrderItem[] {
  return rows.map((row, i) => ({
    lineId: `import-${Date.now()}-${i}`,
    productName: row.productName,
    unit: row.unit,
    quantity: row.quantity,
    costPerUnit: row.costPerUnit,
  }));
}
